// DeepSeek API service for Math Learning Card generation
const DEEPSEEK_API_KEY = 'sk-6d1c0e29faa046ba857b2fcc15abd3b3';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

class DeepSeekMathsCardApiService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  async generateMathsCard(mathTopic, onProgress = null, retryCount = 0) {
    const startTime = Date.now();
    let tokensUsed = 0;
    let estimatedTokens = 0;
    const maxRetries = 2;

    try {
      // Estimate tokens based on topic complexity
      estimatedTokens = this.estimateTokens(mathTopic);
      
      if (onProgress) {
        onProgress({
          stage: 'preparing',
          message: retryCount > 0 ? `準備重新生成數學學習卡片... (重試 ${retryCount}/${maxRetries})` : '準備生成數學學習卡片...',
          tokensUsed: 0,
          estimatedTokens: estimatedTokens,
          timeElapsed: 0
        });
      }

      const prompt = this.createMathsCardPrompt(mathTopic);
      
      if (onProgress) {
        onProgress({
          stage: 'analyzing',
          message: '分析數學主題內容...',
          tokensUsed: 0,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      // Reduced delay for better UX
      await this.delay(500);
      
      if (onProgress) {
        onProgress({
          stage: 'generating',
          message: '正在生成數學學習卡片...',
          tokensUsed: Math.floor(estimatedTokens * 0.3),
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timeout after 60 seconds'));
        }, 60000); // 60 second timeout
      });

      // Race between the API call and timeout
      const response = await Promise.race([
        fetch(this.baseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`,
          },
          body: JSON.stringify({
            model: 'deepseek-chat',
            messages: [
              {
                role: 'system',
                content: this.getMathsCardSystemPrompt()
              },
              {
                role: 'user',
                content: prompt
              }
            ],
            temperature: 0.7,
            max_tokens: 6000, // Increased for detailed HTML content
          })
        }),
        timeoutPromise
      ]);

      if (onProgress) {
        onProgress({
          stage: 'processing',
          message: '處理 AI 回應...',
          tokensUsed: Math.floor(estimatedTokens * 0.6),
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek MathsCard API Error Response:', errorText);
        throw new Error(`DeepSeek MathsCard API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek MathsCard API Response:', data);
      
      // Calculate actual tokens used
      tokensUsed = data.usage?.total_tokens || estimatedTokens;
      
      if (onProgress) {
        onProgress({
          stage: 'validating',
          message: '驗證卡片內容完整性...',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw MathsCard content from API:', content);
      
      // Parse and validate the HTML content
      const validatedContent = this.validateAndFixMathsCardContent(content, mathTopic);
      
      const costIncurred = this.calculateCost(tokensUsed, data.usage);
      
      if (onProgress) {
        onProgress({
          stage: 'completed',
          message: '數學學習卡片生成完成！',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          costIncurred: costIncurred
        });
      }
      
      return {
        success: true,
        htmlContent: validatedContent,
        metadata: {
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          generationTime: Math.floor((Date.now() - startTime) / 1000),
          apiResponse: data,
          validationApplied: true,
          isRealAIGenerated: true,
          costIncurred: costIncurred,
          topic: mathTopic
        }
      };
    } catch (error) {
      console.error('Error generating MathsCard:', error);
      
      // Retry logic for network-related errors
      if (retryCount < maxRetries && (
        error.message.includes('timeout') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('network') ||
        error.message.includes('fetch')
      )) {
        console.log(`Retrying API call (${retryCount + 1}/${maxRetries})...`);
        await this.delay(2000 * (retryCount + 1)); // Exponential backoff
        return this.generateMathsCard(mathTopic, onProgress, retryCount + 1);
      }
      
      // Provide more specific error messages
      let userMessage = '生成數學學習卡片時發生錯誤，請稍後再試。';
      
      if (error.name === 'AbortError') {
        userMessage = '請求被取消，請重試。';
      } else if (error.message.includes('timeout')) {
        userMessage = '請求超時，請檢查網絡連接或稍後再試。';
      } else if (error.message.includes('HTML')) {
        userMessage = 'AI 回應格式錯誤，正在重試...';
      } else if (error.message.includes('API')) {
        userMessage = 'API 連接錯誤，請檢查網絡連接。';
      } else if (error.message.includes('token')) {
        userMessage = 'API 配額已用完，請稍後再試。';
      } else if (error.message.includes('fetch') || error.message.includes('network')) {
        userMessage = '網絡連接失敗，請檢查網絡設置。';
      } else if (error.message.includes('Failed to fetch')) {
        userMessage = '無法連接到 DeepSeek 服務器，請檢查網絡連接。';
      }
      
      throw new Error(userMessage + ' 錯誤詳情：' + error.message);
    }
  }

  getMathsCardSystemPrompt() {
    return `# Role: 数学知识可视化卡片设计师

## Profile:
- Author: 有文
- Version: 2.0
- Description: 你是一位审美卓越、技艺精湛的数学知识可视化卡片设计师。你不仅精通数学原理和前端技术，更擅长运用现代设计美学和色彩心理学，将抽象的数学概念转化为布局优雅、色彩丰富、视觉流畅、且极具吸引力的HTML学习卡片。你的设计旨在点燃用户的学习热情，提供愉悦且高效的理解体验。

## Goals:
1. 根据用户提供的数学知识点名称，生成一份结构化、设计感十足的"数学知识学习卡片"HTML内容。
2. 卡片内容需包含以下关键元素，并在视觉呈现上追求卓越：
   -【知识点名称】:视觉焦点，设计需醒目且融入整体风格。使用大标题和副标题的形式。
   -【核心释义】:清晰易读，排版舒适。运用列表、适度加粗或主题色高亮关键词。
   -【关键公式】:(若适用) 样式化突出，使其既显眼又易于理解。使用专门的公式框，背景色区分。
   -【实例解析】:提供具体例题及分步详解。步骤清晰，版式引导性强。
   -【直观图示】:(核心亮点！) 动态生成与知识点高度相关、数学上严谨且视觉上与卡片设计和谐统一的SVG图形。图形简洁、精确、美观。
3. 生成包含上述内容的单一HTML文件代码，该卡片需达到以下精细化设计要求：
   - 整体风格:现代、优雅、动态、教育化，强调视觉舒适度和信息层级。
   - 数学公式必须清晰显示，不使用LaTeX格式，而是使用标准的数学符号。
   - 使用专业的颜色搭配：深色标题栏，白色内容区，彩色强调元素。
   - 添加导航标签页功能，支持多个相关主题的切换。
4. 在網頁上展示正常數學算式。另外。給我完成「導出為PDF」的功能
5. 繁體顯示。
6. 多圖展示，多圖作講解。
7. 附設接鈕，讓用家把整份完整網頁變成一份PDF（留心若有例子的Tab頁面，你用美感的方向把他們也一併議放在PDF的單一文件內。完成「導出為PDF」的功能。

## Constraints:
- 数学内容绝对准确。注意：數學算式必須在網頁上正常顯示。完全避免以Latext格式演示數學公式，必須以正常數學演算式展示。
- 语言表达清晰、简洁、专业。
- SVG图示紧密关联知识点，有效辅助理解，视觉风格与卡片统一。留心繪畫圓形時，點在圖線時，一定要對準。
- 生成的HTML代码结构良好、语义清晰，所有CSS嵌入<style>标签内。严禁引用外部CSS/JS文件。
- 视觉设计（渐变、区域背景色、布局）必须服务于提升学习兴趣和信息传达效率，确保高度的易读性和视觉和谐性，避免花哨、混乱或干扰性的元素。
- 色彩选择（包括渐变和区域背景）需有逻辑依据并保持整体美感。
- 必须包含导航标签页，支持多个相关主题的切换。
- 数学公式必须使用专门的公式框，背景色区分，确保清晰可读。

## Input Format:
用户将提供需要生成卡片的数学知识点名称。
数学知识点名称: [用户提供的数学主题]`;
  }

  createMathsCardPrompt(mathTopic) {
    return `# Role: 数学知识可视化卡片设计师

## Profile:
- Author: 有文
- Version: 2.0
- Description: 你是一位审美卓越、技艺精湛的数学知识可视化卡片设计师。你不仅精通数学原理和前端技术，更擅长运用现代设计美学和色彩心理学，将抽象的数学概念转化为布局优雅、色彩丰富、视觉流畅、且极具吸引力的HTML学习卡片。你的设计旨在点燃用户的学习热情，提供愉悦且高效的理解体验。

## Goals:
1. 根据用户提供的数学知识点名称，生成一份结构化、设计感十足的"数学知识学习卡片"HTML内容。
2. 卡片内容需包含以下关键元素，并在视觉呈现上追求卓越：
   -【知识点名称】:视觉焦点，设计需醒目且融入整体风格。使用大标题和副标题的形式。
   -【核心释义】:清晰易读，排版舒适。运用列表、适度加粗或主题色高亮关键词。使用专门的content-section样式。
   -【关键公式】:(若适用) 样式化突出，使其既显眼又易于理解。使用专门的math-formula样式，确保数学公式清晰可读。
   -【关键术语】:使用content-section样式，清晰列出重要概念和定义。
   -【直观图示】:(核心亮点！) 使用visualization-section样式，动态生成与知识点高度相关、数学上严谨且视觉上与卡片设计和谐统一的SVG图形。图形简洁、精确、美观。
3. 生成包含上述内容的单一HTML文件代码，该卡片需达到以下精细化设计要求：
   - 整体风格:现代、优雅、动态、教育化，强调视觉舒适度和信息层级。
   - 数学公式必须清晰显示，不使用LaTeX格式，而是使用标准的数学符号和HTML标签（如sup、sub等）。
   - 使用专业的颜色搭配：深色标题栏，白色内容区，彩色强调元素。
   - 添加导航标签页功能，支持多个相关主题的切换。
   - 使用section-title样式来突出各个部分的标题。
   - 确保所有数学公式使用math-formula类，确保清晰可读。
4. 在網頁上展示正常數學算式。另外。給我完成「導出為PDF」的功能
5. 繁體顯示。
6. 多圖展示，多圖作講解。
7. 附設接鈕，讓用家把整份完整網頁變成一份PDF（留心若有例子的Tab頁面，你用美感的方向把他們也一併議放在PDF的單一文件內。完成「導出為PDF」的功能。

## Constraints:
- 数学内容绝对准确。注意：數學算式必須在網頁上正常顯示。完全避免以Latext格式演示數學公式，必須以正常數學演算式展示。
- 语言表达清晰、简洁、专业。
- SVG图示紧密关联知识点，有效辅助理解，视觉风格与卡片统一。留心繪畫圓形時，點在圖線時，一定要對準。
- 生成的HTML代码结构良好、语义清晰，所有CSS嵌入<style>标签内。严禁引用外部CSS/JS文件。
- 视觉设计（渐变、区域背景色、布局）必须服务于提升学习兴趣和信息传达效率，确保高度的易读性和视觉和谐性，避免花哨、混乱或干扰性的元素。
- 色彩选择（包括渐变和区域背景）需有逻辑依据并保持整体美感。
- 必须包含导航标签页，支持多个相关主题的切换。
- 数学公式必须使用math-formula类，确保清晰可读，使用HTML标签如sup、sub等来显示上标和下标。
- 所有内容必须使用content-section类来包装，确保统一的样式和布局。
- 确保生成的HTML具有专业的教育材料外观，类似高质量的数学教材。

## Input Format:
用户将提供需要生成卡片的数学知识点名称。
数学知识点名称: ${mathTopic}

請根據以上要求，生成一個完整的HTML文件，包含：
1. 完整的HTML結構，包含導航標籤頁
2. 內嵌的CSS樣式，使用專業的顏色搭配
3. 數學內容的繁體中文顯示
4. 數學公式使用math-formula類，確保清晰可讀
5. 多個SVG圖形展示
6. PDF導出功能
7. 響應式設計
8. 現代化的視覺效果，包括深色標題欄和白色內容區

請確保生成的HTML文件可以直接在瀏覽器中運行，並且包含所有必要的功能。卡片應該具有專業的外觀，類似於高質量的教育材料。`;
  }

  validateAndFixMathsCardContent(content, mathTopic) {
    if (!content || typeof content !== 'string') {
      throw new Error('API returned empty or invalid content');
    }

    // Check if content contains HTML structure
    if (!content.includes('<html') && !content.includes('<!DOCTYPE')) {
      // If not HTML, wrap it in a basic HTML structure
      content = this.wrapContentInHTML(content, mathTopic);
    }

    // Validate and fix common HTML issues
    let validatedContent = content
      // Ensure proper HTML structure
      .replace(/<!DOCTYPE[^>]*>/gi, '<!DOCTYPE html>')
      .replace(/<html[^>]*>/gi, '<html lang="zh-HK">')
      // Fix common HTML issues
      .replace(/<head>/gi, '<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">')
      // Ensure proper closing tags
      .replace(/<br>/gi, '<br />')
      .replace(/<hr>/gi, '<hr />')
      // Fix common CSS issues
      .replace(/color:\s*#[0-9a-fA-F]{3,6}/gi, 'color: #333')
      .replace(/background-color:\s*#[0-9a-fA-F]{3,6}/gi, 'background-color: #f8f9fa');

    // Ensure PDF export functionality is included
    if (!validatedContent.includes('pdf') && !validatedContent.includes('PDF')) {
      validatedContent = this.addPDFExportFunctionality(validatedContent);
    }

    // Ensure responsive design
    if (!validatedContent.includes('@media')) {
      validatedContent = this.addResponsiveDesign(validatedContent);
    }

    return validatedContent;
  }

  wrapContentInHTML(content, mathTopic) {
    return `<!DOCTYPE html>
<html lang="zh-HK">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>數學學習卡片 - ${mathTopic}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Microsoft JhengHei', 'PingFang TC', 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            background: #f5f5f5;
            min-height: 100vh;
            padding: 20px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        
        .header {
            background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
            color: white;
            padding: 40px;
            text-align: center;
        }
        
        .header h1 {
            font-size: 2.8rem;
            margin-bottom: 10px;
            font-weight: 700;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        
        .header p {
            font-size: 1.3rem;
            opacity: 0.9;
            font-weight: 300;
        }
        
        .content-card {
            background: white;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            margin: 20px;
            overflow: hidden;
        }
        
        .section-title {
            display: flex;
            align-items: center;
            margin-bottom: 20px;
            font-size: 1.5rem;
            font-weight: 600;
            color: #2c3e50;
        }
        
        .section-title::before {
            content: '';
            width: 4px;
            height: 24px;
            background: #3498db;
            margin-right: 12px;
            border-radius: 2px;
        }
        
        .definition-section {
            background: #f8f9fa;
            padding: 25px;
            border-radius: 12px;
            margin-bottom: 30px;
            border-left: 5px solid #3498db;
        }
        
        .formula-section {
            background: #e8f4fd;
            padding: 25px;
            border-radius: 12px;
            margin-bottom: 30px;
            border: 2px solid #3498db;
            position: relative;
        }
        
        .formula-section::before {
            content: '公式';
            position: absolute;
            top: -10px;
            left: 20px;
            background: #3498db;
            color: white;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: 600;
        }
        
        .key-terms-section {
            background: #fff3cd;
            padding: 25px;
            border-radius: 12px;
            margin-bottom: 30px;
            border-left: 5px solid #ffc107;
        }
        
        .key-terms-section h3 {
            color: #856404;
            margin-bottom: 15px;
            font-size: 1.3rem;
        }
        
        .key-terms-section ul {
            list-style: none;
            padding: 0;
        }
        
        .key-terms-section li {
            padding: 8px 0;
            border-bottom: 1px solid #e9ecef;
        }
        
        .key-terms-section li:last-child {
            border-bottom: none;
        }
        
        .visualization-section {
            background: #d1ecf1;
            padding: 25px;
            border-radius: 12px;
            margin-bottom: 30px;
            border-left: 5px solid #17a2b8;
            text-align: center;
        }
        
        .visualization-section h3 {
            color: #0c5460;
            margin-bottom: 15px;
            font-size: 1.3rem;
        }
        
        .svg-graph {
            background: white;
            border-radius: 10px;
            padding: 20px;
            margin: 20px 0;
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
            border: 1px solid #e9ecef;
        }
        
        .math-formula {
            font-family: 'Courier New', monospace;
            font-size: 1.1rem;
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            border: 1px solid #e9ecef;
            margin: 10px 0;
            text-align: center;
            font-weight: 500;
        }
        
        .math-formula sup {
            font-size: 0.8em;
            vertical-align: super;
        }
        
        .math-formula sub {
            font-size: 0.8em;
            vertical-align: sub;
        }
        
        .content-section {
            background: white;
            padding: 30px;
            border-radius: 12px;
            margin-bottom: 25px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .content-section h2 {
            color: #2c3e50;
            font-size: 1.8rem;
            margin-bottom: 20px;
            font-weight: 600;
            display: flex;
            align-items: center;
        }
        
        .content-section h2::before {
            content: '';
            width: 4px;
            height: 24px;
            background: #3498db;
            margin-right: 12px;
            border-radius: 2px;
        }
        
        .content-section p {
            line-height: 1.8;
            margin-bottom: 15px;
            color: #333;
        }
        
        .content-section ul {
            margin-left: 20px;
            margin-bottom: 15px;
        }
        
        .content-section li {
            margin-bottom: 8px;
            line-height: 1.6;
        }
        
        .nav-tabs {
            background: #ecf0f1;
            padding: 0;
            border-bottom: 1px solid #bdc3c7;
        }
        
        .nav-tabs ul {
            list-style: none;
            display: flex;
            margin: 0;
            padding: 0;
        }
        
        .nav-tabs li {
            flex: 1;
        }
        
        .nav-tabs button {
            width: 100%;
            padding: 15px 20px;
            background: #ecf0f1;
            border: none;
            cursor: pointer;
            font-size: 1.1rem;
            font-weight: 500;
            color: #2c3e50;
            transition: all 0.3s ease;
            border-radius: 8px 8px 0 0;
        }
        
        .nav-tabs button.active {
            background: white;
            color: #3498db;
            border-bottom: 3px solid #3498db;
        }
        
        .nav-tabs button:hover {
            background: #d5dbdb;
        }
        
        .content {
            padding: 40px;
        }
        
        .section {
            margin-bottom: 40px;
            padding: 30px;
            background: #f8f9fa;
            border-radius: 12px;
            border-left: 5px solid #3498db;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .section h2 {
            color: #2c3e50;
            font-size: 1.8rem;
            margin-bottom: 20px;
            font-weight: 600;
            display: flex;
            align-items: center;
        }
        
        .section h2::before {
            content: '';
            width: 4px;
            height: 24px;
            background: #3498db;
            margin-right: 12px;
            border-radius: 2px;
        }
        
        .formula-box {
            background: #e8f4fd;
            padding: 20px;
            border-radius: 10px;
            margin: 20px 0;
            border: 2px solid #3498db;
            font-family: 'Courier New', monospace;
            font-size: 1.1rem;
            text-align: center;
            position: relative;
        }
        
        .formula-box::before {
            content: '公式';
            position: absolute;
            top: -10px;
            left: 20px;
            background: #3498db;
            color: white;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: 600;
        }
        
        .definition-box {
            background: #fff3cd;
            padding: 20px;
            border-radius: 10px;
            margin: 20px 0;
            border-left: 5px solid #ffc107;
        }
        
        .definition-box h3 {
            color: #856404;
            margin-bottom: 15px;
            font-size: 1.3rem;
        }
        
        .example {
            background: #d1ecf1;
            padding: 25px;
            border-radius: 10px;
            margin: 20px 0;
            border-left: 5px solid #17a2b8;
        }
        
        .example h3 {
            color: #0c5460;
            margin-bottom: 15px;
            font-size: 1.3rem;
        }
        
        .svg-container {
            text-align: center;
            margin: 30px 0;
            padding: 20px;
            background: white;
            border-radius: 10px;
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
            border: 1px solid #e9ecef;
        }
        
        .key-terms {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 10px;
            margin: 20px 0;
            border-left: 5px solid #28a745;
        }
        
        .key-terms h3 {
            color: #155724;
            margin-bottom: 15px;
            font-size: 1.3rem;
        }
        
        .key-terms ul {
            list-style: none;
            padding: 0;
        }
        
        .key-terms li {
            padding: 8px 0;
            border-bottom: 1px solid #e9ecef;
        }
        
        .key-terms li:last-child {
            border-bottom: none;
        }
        
        .pdf-button {
            position: fixed;
            bottom: 30px;
            right: 30px;
            background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 50px;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 10px 20px rgba(0,0,0,0.2);
            transition: all 0.3s ease;
            z-index: 1000;
        }
        
        .pdf-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 30px rgba(0,0,0,0.3);
        }
        
        .tab-content {
            display: none;
        }
        
        .tab-content.active {
            display: block;
        }
        
        @media (max-width: 768px) {
            body {
                padding: 10px;
            }
            
            .header {
                padding: 20px;
            }
            
            .header h1 {
                font-size: 2rem;
            }
            
            .content {
                padding: 20px;
            }
            
            .section {
                padding: 20px;
            }
            
            .nav-tabs ul {
                flex-direction: column;
            }
            
            .nav-tabs button {
                border-radius: 0;
            }
            
            .pdf-button {
                bottom: 20px;
                right: 20px;
                padding: 12px 24px;
                font-size: 1rem;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${mathTopic}</h1>
            <p>數學序列的基本概念與求和應用</p>
        </div>
        
        <div class="nav-tabs">
            <ul>
                <li><button class="tab-button active" onclick="showTab('main')">${mathTopic.split(' ')[0] || '主要內容'}</button></li>
                <li><button class="tab-button" onclick="showTab('examples')">實例解析</button></li>
                <li><button class="tab-button" onclick="showTab('applications')">實際應用</button></li>
            </ul>
        </div>
        
        <div class="content">
            <div class="content-card">
                <div id="main" class="tab-content active">
                    ${content}
                </div>
                <div id="examples" class="tab-content">
                    <div class="section">
                        <h2>實例解析</h2>
                        <p>這裡將顯示相關的實例解析內容...</p>
                    </div>
                </div>
                <div id="applications" class="tab-content">
                    <div class="section">
                        <h2>實際應用</h2>
                        <p>這裡將顯示實際應用內容...</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
    
    <button class="pdf-button" onclick="exportToPDF()">
        📄 導出為 PDF
    </button>
    
    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
    <script>
        function showTab(tabName) {
            // Hide all tab contents
            const tabContents = document.querySelectorAll('.tab-content');
            tabContents.forEach(content => content.classList.remove('active'));
            
            // Remove active class from all buttons
            const tabButtons = document.querySelectorAll('.tab-button');
            tabButtons.forEach(button => button.classList.remove('active'));
            
            // Show selected tab content
            document.getElementById(tabName).classList.add('active');
            
            // Add active class to clicked button
            event.target.classList.add('active');
        }
        
        function exportToPDF() {
            const element = document.querySelector('.container');
            const opt = {
                margin: 1,
                filename: '數學學習卡片_${mathTopic}.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            
            html2pdf().set(opt).from(element).save();
        }
    </script>
</body>
</html>`;
  }

  addPDFExportFunctionality(content) {
    const pdfScript = `
    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
    <script>
        function exportToPDF() {
            const element = document.querySelector('.container') || document.body;
            const opt = {
                margin: 1,
                filename: '數學學習卡片.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            
            html2pdf().set(opt).from(element).save();
        }
    </script>`;
    
    // Add PDF button if not present
    if (!content.includes('pdf-button')) {
      const pdfButton = `
      <button class="pdf-button" onclick="exportToPDF()" style="
          position: fixed;
          bottom: 30px;
          right: 30px;
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 50px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
          z-index: 1000;
      ">
          📄 導出為 PDF
      </button>`;
      
      content = content.replace('</body>', pdfButton + pdfScript + '</body>');
    }
    
    return content;
  }

  addResponsiveDesign(content) {
    const responsiveCSS = `
        @media (max-width: 768px) {
            body {
                padding: 10px;
            }
            
            .header {
                padding: 20px;
            }
            
            .header h1 {
                font-size: 2rem;
            }
            
            .content {
                padding: 20px;
            }
            
            .section {
                padding: 20px;
            }
            
            .nav-tabs ul {
                flex-direction: column;
            }
            
            .nav-tabs button {
                border-radius: 0;
            }
            
            .pdf-button {
                bottom: 20px;
                right: 20px;
                padding: 12px 24px;
                font-size: 1rem;
            }
        }
    `;
    
    // Add responsive CSS to existing style tag
    content = content.replace('</style>', responsiveCSS + '</style>');
    
    return content;
  }

  estimateTokens(mathTopic) {
    // Estimate tokens based on topic complexity
    const topicLength = mathTopic.length;
    const baseTokens = 1000; // Base tokens for prompt
    const topicTokens = Math.ceil(topicLength / 4);
    const responseTokens = 4000; // Estimated response tokens for detailed HTML
    
    return baseTokens + topicTokens + responseTokens;
  }

  calculateCost(tokensUsed, usage) {
    if (!usage) return 0;
    
    const inputTokens = usage.prompt_tokens || 0;
    const outputTokens = usage.completion_tokens || 0;
    
    // DeepSeek API pricing (in RMB per million tokens)
    // Standard time pricing (Beijing time 08:30-00:30)
    const standardInputPrice = 2.0; // Standard time, cache miss (deepseek-chat)
    const standardOutputPrice = 8.0; // Standard time (deepseek-chat)
    
    // Discount time pricing (Beijing time 00:30-08:30) - 50% discount
    const currentHour = new Date().getHours();
    const isDiscountTime = currentHour >= 0 && currentHour < 8.5;
    
    const finalInputPrice = isDiscountTime ? standardInputPrice * 0.5 : standardInputPrice;
    const finalOutputPrice = isDiscountTime ? standardOutputPrice * 0.5 : standardOutputPrice;
    
    const inputCost = (inputTokens / 1000000) * finalInputPrice;
    const outputCost = (outputTokens / 1000000) * finalOutputPrice;
    
    return inputCost + outputCost;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

const deepSeekMathsCardApiService = new DeepSeekMathsCardApiService();
export default deepSeekMathsCardApiService;